import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Col,
  DatePicker,
  Modal,
  Form,
  Row,
  Select,
  InputNumber,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import { QualityLineProps } from "../../../interfaces/interfaces";
import DataContext from "../../../context/DataContext";
import { order_line_initial_state } from "../../../utils/data";
import { UserModal } from "../user";
import { UserAddOutlined } from "@ant-design/icons";

interface Props {
  index: number;
  open: boolean;
  valuesEdit: QualityLineProps;
  isEdition?: boolean;
  orderId: number | null;
  QualityLinesLength: number;
  onAddOrderLine?: (values: QualityLineProps) => void;
  onEditOrderLine?: (values: QualityLineProps) => void;
  onClose: () => void;
}

const format = "MM/DD/YYYY";

export const OrderLineForm = ({
  index,
  open,
  isEdition,
  orderId,
  QualityLinesLength,
  valuesEdit,
  onAddOrderLine,
  onEditOrderLine,
  onClose,
}: Props) => {
  const [form] = Form.useForm();
  const [openUserModal, setOpenUserModal] = useState(false);
  const { userList } = useContext(DataContext);
  const [values, setValues] = useState(order_line_initial_state);

  useEffect(() => {
    if (valuesEdit) {
      form.setFieldsValue(valuesEdit);
      setValues(valuesEdit);
    }
  }, [valuesEdit]);

  const filterOption = (
    input: string,
    option?: { label: string; value: string },
  ) =>
    ((option && option.label) || "")
      .toLowerCase()
      .includes(input.toLowerCase());

  const filterOptionUser = (
    input: string,
    option?: { Name: string; value: string },
  ) =>
    ((option && option.Name) || "").toLowerCase().includes(input.toLowerCase());

  return (
    <>
      <Modal
        title={isEdition ? "Edit" : "Create"}
        width={600}
        open={open}
        onCancel={onClose}
        maskClosable={false}
        footer={
          <Row justify="end" gutter={8}>
            <Col>
              <Button onClick={onClose} danger>
                Cancel
              </Button>
            </Col>
            <Col>
              <Button
                htmlType="submit"
                type="primary"
                onClick={() => {
                  form
                    .validateFields()
                    .then(() => {
                      isEdition
                        ? onEditOrderLine && onEditOrderLine(values)
                        : onAddOrderLine &&
                          onAddOrderLine({
                            ...values,
                            Order: orderId ?? 0,
                            OID: QualityLinesLength + 1,
                          });
                    })
                    .then(onClose)
                    .catch((errorInfo) =>
                      console.log("Validation Error:", errorInfo),
                    );
                }}
              >
                Save
              </Button>
            </Col>
          </Row>
        }
      >
        <Form layout="vertical" form={form} initialValues={values}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item
                label="Machinist"
                name="Employee"
                rules={[
                  ({}) => ({
                    validator(_, value) {
                      if (value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error("Please select an User"));
                    },
                  }),
                ]}
              >
                <Row gutter={8}>
                  <Col flex="auto">
                    <Select
                      showSearch
                      placeholder="Select a Machinist"
                      optionFilterProp="children"
                      filterOption={filterOptionUser}
                      style={{ width: "100%" }}
                      onChange={(value) => {
                        const userSelected = userList.find(
                          (user) => user.OID === value,
                        );
                        if (userSelected) {
                          setValues((prev) => ({
                            ...prev,
                            Employee: value,
                            userDetail: userSelected,
                          }));

                          // Update the form state
                          form.setFieldsValue({ Employee: value });
                        }
                      }}
                      value={form.getFieldValue("Employee")} // Ensure form state is used
                      fieldNames={{ label: "Name", value: "OID" }}
                      options={userList as any}
                    />
                  </Col>
                  <Col>
                    <Tooltip title="Add New User">
                      <Button
                        type="primary"
                        onClick={() => setOpenUserModal(true)}
                        icon={<UserAddOutlined />}
                      ></Button>
                    </Tooltip>
                  </Col>
                </Row>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Date"
                rules={[{ required: true, message: "Please choose the Date" }]}
              >
                <DatePicker
                  name="Date"
                  format={format}
                  style={{ width: "100%" }}
                  allowClear={true}
                  value={values.Date && dayjs(values.Date)}
                  onChange={(_, dateString) => {
                    setValues((prev) => {
                      return {
                        ...prev,
                        Date: dateString ? dayjs(dateString as any) : null,
                      };
                    });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="PIN/BOX" name="PinBox">
                <Select
                  showSearch
                  placeholder="PIN/BOX"
                  optionFilterProp="children"
                  filterOption={filterOption}
                  style={{ width: "100%" }}
                  onChange={(value) =>
                    setValues((prev) => ({ ...prev, PinBox: value }))
                  }
                  options={[
                    { label: "PIN", value: "pin" },
                    { label: "BOX", value: "box" },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="Gauge" name="Gauge">
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  value={values.Gauge}
                  formatter={(value) =>
                    value ? value.toString().replace(",", ".") : ""
                  }
                  parser={(value) =>
                    parseFloat(value ? value.replace(",", ".") : "")
                  }
                  onChange={(newValue) => {
                    if (newValue)
                      setValues((prev) => {
                        return {
                          ...prev,
                          Gauge: newValue,
                        };
                      });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Length" name="Length">
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    formatter={(value) =>
                      value ? value.toString().replace(",", ".") : ""
                    }
                    parser={(value) =>
                      parseFloat(value ? value.replace(",", ".") : "")
                    }
                    value={values.Length}
                    onChange={(newValue) => {
                      if (newValue)
                        setValues((prev) => {
                          return {
                            ...prev,
                            Length: newValue,
                          };
                        });
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Nose Diameter" name="NoseDiameter">
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    formatter={(value) =>
                      value ? value.toString().replace(",", ".") : ""
                    }
                    parser={(value) =>
                      parseFloat(value ? value.replace(",", ".") : "")
                    }
                    value={values.NoseDiameter}
                    onChange={(newValue) => {
                      if (newValue)
                        setValues((prev) => {
                          return {
                            ...prev,
                            NoseDiameter: newValue,
                          };
                        });
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Cylinder Diameter" name="CylinderDiameter">
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    formatter={(value) =>
                      value ? value.toString().replace(",", ".") : ""
                    }
                    parser={(value) =>
                      parseFloat(value ? value.replace(",", ".") : "")
                    }
                    value={values.CylinderDiameter}
                    onChange={(newValue) => {
                      if (newValue || newValue === 0)
                        setValues((prev) => {
                          return {
                            ...prev,
                            CylinderDiameter: newValue,
                          };
                        });
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Lead" name="Lead">
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    formatter={(value) =>
                      value ? value.toString().replace(",", ".") : ""
                    }
                    parser={(value) =>
                      parseFloat(value ? value.replace(",", ".") : "")
                    }
                    value={values.Lead}
                    onChange={(newValue) => {
                      if (newValue)
                        setValues((prev) => {
                          return {
                            ...prev,
                            Lead: newValue,
                          };
                        });
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Taper" name="Taper">
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    formatter={(value) =>
                      value ? value.toString().replace(",", ".") : ""
                    }
                    parser={(value) =>
                      parseFloat(value ? value.replace(",", ".") : "")
                    }
                    value={values.Taper}
                    onChange={(newValue) => {
                      if (newValue || newValue === 0)
                        setValues((prev) => {
                          return {
                            ...prev,
                            Taper: newValue,
                          };
                        });
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Bevel Diameter" name="BevelDiameter">
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    formatter={(value) =>
                      value ? value.toString().replace(",", ".") : ""
                    }
                    parser={(value) =>
                      parseFloat(value ? value.replace(",", ".") : "")
                    }
                    value={values.BevelDiameter}
                    onChange={(newValue) => {
                      if (newValue)
                        setValues((prev) => {
                          return {
                            ...prev,
                            BevelDiameter: newValue,
                          };
                        });
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        </Form>
      </Modal>
      <UserModal
        open={openUserModal}
        onClose={() => {
          setOpenUserModal(false);
        }}
      />
    </>
  );
};
